import store from "../../../../../../../../store";
import {setWizardSelectedValues} from "../../../../../../../../actions/wizardFunctions";
import {getCurrentLayout} from "../../../../../../../appLayout";
import CabinetSwitchers from "../../../../../../../kvComponent/navigation/cabinetSwitchers";
import {
  SQFT_PARENT_FEATURE_GROUP_PART_IDS,
  SHAPE_PART_ID,
} from "../../../../../../constants";
import {
  getAvailableSteps,
  getCurrentWizardNavItems,
  getFeatureGroupFromWizardGroup,
} from "../../../../../../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";
import {
  selectedWizardGroupIsNotInCurrentStepAnymore
} from "../../../../../../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions2";

const BOTTOM_CABINET_GROUP_NAME = "Bottom Cabinet";
const MAX_WIZARD_STEPS_TO_RESOLVE = 50;

const getWizardSelectedFeaturesByPartId = (wizardSelectedValues, partId) => {
  if (!wizardSelectedValues || !partId) {
    return [];
  }

  return Object.keys(wizardSelectedValues).reduce((features, stepIndex) => {
    const selectedFeature = wizardSelectedValues[stepIndex]?.selectedFeature;

    if (selectedFeature?.partId === partId) {
      features.push(selectedFeature);
    }

    return features;
  }, []);
};

const hasWizardSelectedFeaturePartId = (wizardSelectedValues, partId) => {
  return getWizardSelectedFeaturesByPartId(wizardSelectedValues, partId).length > 0;
};

const getNormalizedFeatureWords = (feature) => {
  const getFeatureName = (feature) => {
    return feature?.getFeatureName?.() || feature?.partName || "";
  };

  return getFeatureName(feature)
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
};

const getCabinetLineupKey = (feature) => {
  const words = getNormalizedFeatureWords(feature);
  const numericLineup = words.find(word => /^\d+$/.test(word));

  if (numericLineup) {
    return numericLineup;
  }

  return words.slice(0, 2).join(" ");
};

const featureNameHasLineupKey = (feature, lineupKey) => {
  if (!lineupKey) {
    return false;
  }

  const words = getNormalizedFeatureWords(feature);

  if (/^\d+$/.test(lineupKey)) {
    return words.includes(lineupKey);
  }

  return words.join(" ").includes(lineupKey);
};

const getFeatureSharedName = (feature) => {
  return feature?.dbFeature?.sharedFeatureName;
};

const getCabinetSwitcherName = (feature) => {
  if (!feature) {
    return null;
  }

  const splittedFeatureName = feature.getSplittedFeatureName?.();

  return splittedFeatureName
    ? splittedFeatureName.replace(`${feature.dbFeature.groupName} - `, "")
    : null;
};

const getFeatureId = (feature) => {
  return feature?.dbFeature?.id;
};

const getActiveTopCabinetFeature = (layout) => {
  const topCabinetFeatureGroup = layout.getFeatureGroupByPartId(SHAPE_PART_ID.top_cabinet);
  return topCabinetFeatureGroup?.activeFeature;
};

const featureMatchesCandidate = (candidateFeature, feature) => {
  const featureId = getFeatureId(feature);
  const featureSharedName = getFeatureSharedName(feature);
  const candidateSharedName = getFeatureSharedName(candidateFeature);
  const featureSwitcherName = getCabinetSwitcherName(feature);
  const candidateSwitcherName = getCabinetSwitcherName(candidateFeature);

  return getFeatureId(candidateFeature) === featureId
    || (featureSharedName && candidateSharedName === featureSharedName)
    || (featureSwitcherName && candidateSwitcherName === featureSwitcherName);
};

const featureLineupMatchesCandidate = (candidateFeature, feature) => {
  return featureNameHasLineupKey(candidateFeature, getCabinetLineupKey(feature));
};

const getMatchingWizardNavItem = (navItems, feature) => {
  const directMatch = navItems.find(navItem => featureMatchesCandidate(navItem.part, feature));

  if (directMatch) {
    return directMatch;
  }

  const colorOptionMatch = navItems.reduce((matchedNavItem, navItem) => {
    if (matchedNavItem) {
      return matchedNavItem;
    }

    const matchedColorOption = navItem.colorOptions?.find(option => {
      return featureMatchesCandidate(option.feature, feature);
    });

    return matchedColorOption
      ? Object.assign({}, navItem, {part: matchedColorOption.feature})
      : null;
  }, null);

  if (colorOptionMatch) {
    return colorOptionMatch;
  }

  return navItems.find(navItem => featureLineupMatchesCandidate(navItem.part, feature));
};

/**
 * The Bottom Cabinet wizard is split into style/profile and color steps, while
 * SQFT shapes receive a concrete cabinet SKU. Convert selected Top Cabinet
 * wizard features into the equivalent Bottom Cabinet feature before matching
 * each Bottom Cabinet wizard step.
 */
const getEquivalentBottomCabinetFeature = (layout, feature) => {
  const bottomCabinetFeatureGroup = layout.getFeatureGroupByPartId(SHAPE_PART_ID.bottom_cabinet);
  const cabinetSwitcherName = getCabinetSwitcherName(feature);

  if (bottomCabinetFeatureGroup && cabinetSwitcherName) {
    const switcherMatchedFeature = CabinetSwitchers.getNeededFeatureFromNavItems(
      cabinetSwitcherName,
      bottomCabinetFeatureGroup
    );

    if (switcherMatchedFeature) {
      return switcherMatchedFeature;
    }
  }

  const sharedFeatureName = getFeatureSharedName(feature);
  if (!sharedFeatureName) {
    return null;
  }

  return layout.getFeatureBySku(sharedFeatureName, SHAPE_PART_ID.bottom_cabinet, false, true);
};

const getMatchingBottomWizardNavItem = (layout, navItems, feature, selectedValues) => {
  const selectedTopCabinetFeatures = getWizardSelectedFeaturesByPartId(selectedValues, SHAPE_PART_ID.top_cabinet);
  const featuresToMatch = selectedTopCabinetFeatures.reduce((features, selectedFeature) => {
    const bottomFeature = getEquivalentBottomCabinetFeature(layout, selectedFeature);

    if (bottomFeature) {
      features.push(bottomFeature);
    }

    features.push(selectedFeature);
    return features;
  }, [feature]);

  return featuresToMatch.reduce((matchedNavItem, featureToMatch) => {
    return matchedNavItem || getMatchingWizardNavItem(navItems, featureToMatch);
  }, null);
};

const getAutoSkippedWizardValue = (wizardGroup) => ({
  wizardGroup,
  isAutoSkippedStep: true,
});

const getSelectedWizardFeatureValue = (wizardGroup, navItem, navItems) => ({
  wizardGroup,
  selectedFeature: navItem.part,
  partId: navItem.part.partId,
  singleFeature: navItems.length === 1,
});

/**
 * Replays only the wizard path needed to select Bottom Cabinet. Steps before
 * Bottom Cabinet may need to be auto-skipped to reveal it, but once the bottom
 * style/color run is selected we stop so unrelated later groups remain untouched.
 */
const buildWizardValuesThroughFeature = (layout, feature) => {
  const {wizardState} = store.getState();

  if (!wizardState.wizardIsActive || !wizardState.wizardActiveItem || !feature) {
    return null;
  }

  const wizardItem = wizardState.wizardActiveItem;
  const selectedValues = Object.assign({}, wizardState.wizardSelectedValues);
  let hasSelectedBottomCabinetStep = false;

  for (let stepIndex = 0; stepIndex < MAX_WIZARD_STEPS_TO_RESOLVE; stepIndex++) {
    const wizardSteps = getAvailableSteps(wizardItem, selectedValues, true);
    const wizardStep = wizardSteps[stepIndex];
    const wizardGroup = wizardStep?.stepGroups?.[0];

    if (!wizardGroup) {
      break;
    }

    if (wizardGroup.groupName !== BOTTOM_CABINET_GROUP_NAME) {
      if (hasSelectedBottomCabinetStep) {
        return selectedValues;
      }

      if (!selectedValues[stepIndex]
        || selectedWizardGroupIsNotInCurrentStepAnymore(selectedValues[stepIndex], wizardStep.stepGroups)) {
        selectedValues[stepIndex] = getAutoSkippedWizardValue(wizardGroup);
      }
      continue;
    }

    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup, layout);
    const navItems = getCurrentWizardNavItems(
      selectedValues,
      stepIndex,
      wizardItem,
      wizardGroup,
      featureGroup
    );
    const matchingNavItem = getMatchingBottomWizardNavItem(layout, navItems, feature, selectedValues);

    if (matchingNavItem?.part) {
      selectedValues[stepIndex] = getSelectedWizardFeatureValue(wizardGroup, matchingNavItem, navItems);
      hasSelectedBottomCabinetStep = true;
    } else if (!selectedValues[stepIndex]) {
      selectedValues[stepIndex] = getAutoSkippedWizardValue(wizardGroup);
    }
  }

  return hasSelectedBottomCabinetStep ? selectedValues : null;
};

const activateFeatureInWizard = (layout, feature) => {
  const wizardValues = buildWizardValuesThroughFeature(layout, feature);
  if (wizardValues) {
    store.dispatch(setWizardSelectedValues(
      wizardValues,
      true,
      store.getState().wizardState.wizardActiveItem
    ));
  }

  if (typeof feature.clickFromAllWalls === "function") {
    feature.clickFromAllWalls(true);
  }

  return feature;
};

const getMatchingBottomCabinetFeatureFromTop = (layout) => {
  const bottomCabinetFeatureGroup = layout.getFeatureGroupByPartId(SHAPE_PART_ID.bottom_cabinet);
  const topCabinetFeature = getActiveTopCabinetFeature(layout);

  if (!bottomCabinetFeatureGroup?.features || !topCabinetFeature) {
    return null;
  }

  const matchingBottomCabinetFeature = getEquivalentBottomCabinetFeature(layout, topCabinetFeature);

  return matchingBottomCabinetFeature
    ? activateFeatureInWizard(layout, matchingBottomCabinetFeature)
    : null;
};

/**
 * Resolves the wizard parent feature used by SQFT cabinet-box shapes.
 *
 * Lower cabinet boxes normally depend on the Bottom Cabinet wizard feature, but
 * that step can be skipped when it is not required. In that case the lower box
 * should still use the Bottom Cabinet feature that matches the selected Top
 * Cabinet style/color, instead of falling back to a stale or default Bottom
 * Cabinet activeFeature. If Bottom Cabinet was explicitly selected in the
 * wizard, the Bottom Cabinet feature group's activeFeature wins because several
 * wizard steps can share the same partId.
 */
export const getResolvedCabinetParentFeature = ({
  feature,
  parentFeature,
  partId,
  wizardSelectedValues = store.getState().wizardState.wizardSelectedValues,
  layout = getCurrentLayout(),
}) => {
  const currentParentPartId = partId
    || SQFT_PARENT_FEATURE_GROUP_PART_IDS[feature?.partId]
    || parentFeature?.partId;

  if (currentParentPartId !== SHAPE_PART_ID.bottom_cabinet) {
    return parentFeature;
  }

  const hasSelectedBottomCabinetFeature = hasWizardSelectedFeaturePartId(
    wizardSelectedValues,
    SHAPE_PART_ID.bottom_cabinet
  );

  if (hasSelectedBottomCabinetFeature) {
    return parentFeature;
  }

  return getMatchingBottomCabinetFeatureFromTop(layout) || parentFeature;
};
